/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import DynamicFieldsModel from '@linc-technologies/linced-common/models/dynamic-field/base-model';
import { warn } from '@ember/debug';
import { get, computed } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { cached } from '@glimmer/tracking';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr } from '@warp-drive/legacy/model';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import {
  array,
  fragment,
  fragmentArray,
} from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import StaffPageAccess from '@linc-technologies/linced-common/enums/roles/staff-page-access';
import SchoolStatus from '@linc-technologies/linced-common/enums/school-status';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import type Caregiver from '@linc-technologies/linced-common/models/people/caregiver';
import type Learner from '@linc-technologies/linced-common/models/people/learner';
import type Medical from '@linc-technologies/linced-common/models/people/medical';
import type Options from '@linc-technologies/linced-common/models/people/options';
import type PolicyAcceptance from '@linc-technologies/linced-common/models/people/policy-acceptance';
import type PostCounts from '@linc-technologies/linced-common/models/people/post-counts';
import type Staff from '@linc-technologies/linced-common/models/people/staff';
import type FieldOptionsService from '@linc-technologies/linced-common/services/field-options';
import type PermissionsService from '@linc-technologies/linced-common/services/permissions';
import type SessionService from '@linc-technologies/linced-common/services/session';
import { compact, findBy } from '@linc-technologies/linced-common/utils/arrays';
import { dynamicField } from '@linc-technologies/linced-common/utils/dynamic-fields';
import moment from 'moment';

// TS-STANDIN(services/field-options): the service is JS, so its
// yearLevelOptions is any. These types list what the person reads.
interface YearLevelOption {
  valueInt: number;
  label: string;
}

interface YearLevelOptionMap {
  schoolId: string;
  options: YearLevelOption[];
}

// The fields of a school date (the linced-app date model) that
// weeksAtSchoolOnDate reads.
interface SchoolDate {
  date: string | undefined;
  term: number;
  status: number;
}

// Proto: people.Person.
export default class Person extends DynamicFieldsModel {
  declare [Type]: 'person';

  @service
  declare session: SessionService;

  @service
  declare fieldOptions: FieldOptionsService;

  @service
  declare permissions: PermissionsService;

  @attr('number', { defaultValue: 0 })
  declare type: EnumValue<typeof PersonType> | 0;

  @attr('number', { defaultValue: 0 })
  declare jurisdiction: EnumValue<typeof Jurisdiction> | 0;

  @attr('string', { defaultValue: '' })
  declare jurisdictionalId: string;

  @attr('number', { defaultValue: 0 })
  declare heroId: number;

  @fragment('people/options', { defaultValue: () => ({}) })
  declare options: Options | null;

  @attr('boolean', { defaultValue: false })
  declare isDemo: boolean;

  @fragment('people/learner')
  declare learner: Learner | null;

  @fragment('people/caregiver')
  declare caregiver: Caregiver | null;

  @fragment('people/medical')
  declare medical: Medical | null;

  @fragment('people/staff')
  declare staff: Staff | null;

  @computed('type', 'learner.schoolId')
  get school_id(): string {
    return this.type === PersonType.LEARNER
      ? (this.learner?.schoolId ?? '')
      : '';
  }

  @attr('string', { defaultValue: '' })
  declare profileMediaId: string;

  @alias('profileMediaId')
  declare profileMedia: string | undefined;

  @alias('profileMediaId')
  declare mediaId: string | undefined; // required by AvatarImage component

  // Deprecated: policyAcceptanceDate indicates the date the person last accepted Hero's policy changes
  // formatted as YYYY-MM-DD
  @attr('string')
  declare policyAcceptanceDate: string | undefined;

  // policyAcceptances records acceptance of each individual policy. See
  // utils/policies for the policy definitions and helpers.
  @fragmentArray('people/policy-acceptance')
  declare policyAcceptances: FragmentArray<PolicyAcceptance>;

  // The getters that read dynamic fields are autotracked (@cached) and
  // usable as classic dependent keys (@dependentKeyCompat).
  @dependentKeyCompat
  @cached
  get legalFirstName() {
    if (this.type !== 1) {
      // Not a Learner - just return their firstName
      return this.fieldValue<string>('core:firstName') ?? '';
    }
    return this.fieldValue<string>('core:firstNameLegal') ?? '';
  }

  @dependentKeyCompat
  @cached
  get legalLastName() {
    if (this.type !== 1) {
      // Not a Learner - just return their firstName
      return this.fieldValue<string>('core:lastName') ?? '';
    }
    return this.fieldValue<string>('core:lastNameLegal') ?? '';
  }

  @dependentKeyCompat
  @cached
  get firstName() {
    if (this.type !== 1) {
      // Not a Learner - just return their firstName
      return this.fieldValue<string>('core:firstName') ?? '';
    }
    const legal = this.fieldValue<string>('core:firstNameLegal') ?? '';
    const preferred = this.fieldValue<string>('core:firstNamePreferred') ?? '';

    return isEmpty(preferred) ? legal : preferred;
  }

  @dependentKeyCompat
  @cached
  get lastName() {
    if (this.type !== 1) {
      // Not a Learner - just return their lastName
      return this.fieldValue<string>('core:lastName') ?? '';
    }
    const legal = this.fieldValue<string>('core:lastNameLegal') ?? '';
    const preferred = this.fieldValue<string>('core:lastNamePreferred') ?? '';

    return isEmpty(preferred) ? legal : preferred;
  }

  @computed('firstName', 'lastName')
  get fullName() {
    return `${this.firstName} ${this.lastName}`.trim();
  }

  @computed('legalFirstName', 'legalLastName')
  get legalFullName() {
    return `${this.legalFirstName} ${this.legalLastName}`.trim();
  }

  @computed('firstName')
  get firstInitial() {
    return (this.get('firstName') ?? '').charAt(0);
  }

  @computed('lastName')
  get lastInitial() {
    return (this.get('lastName') ?? '').charAt(0);
  }

  @dependentKeyCompat
  get legalFirstInitial() {
    return this.legalFirstName.charAt(0).toUpperCase();
  }

  @dependentKeyCompat
  get legalLastInitial() {
    return this.legalLastName.charAt(0).toUpperCase();
  }

  @dynamicField('core:dob', DataType.STRING)
  declare dob: string | undefined;

  @computed('dob')
  get ageYears() {
    return moment().diff(this.dob, 'years');
  }

  @computed('dob')
  get ageMonths() {
    return moment().diff(this.dob, 'months') % 12;
  }

  @dependentKeyCompat
  @cached
  get firstSchooling() {
    const defaultField = 'core:firstschoolingDate';
    let field = defaultField;
    switch (this.jurisdiction) {
      case Jurisdiction.NZ_MOE:
        field = 'nzMOE:firstSchoolingDate';
        break;
    }
    let firstSchooling = this.fieldValue<string>(field);
    if (isEmpty(firstSchooling) && field !== defaultField) {
      firstSchooling = this.fieldValue<string>(defaultField);
    }
    return firstSchooling;
  }

  @computed('firstSchooling')
  get weeksAtSchool() {
    return moment().diff(this.firstSchooling, 'weeks');
  }

  @computed('firstSchooling')
  get monthsAtSchool() {
    return moment().diff(this.firstSchooling, 'months');
  }

  monthsAtSchoolOnDate(date: string) {
    if (this.firstSchooling) {
      const months = moment(date).diff(this.firstSchooling, 'months');
      if (months < 0) {
        return;
      }

      return months;
    }
  }

  // Without dates, the weeks include the holidays.
  weeksAtSchoolOnDate(
    asOnDate: string,
    dates?: readonly SchoolDate[],
    excludingHolidays = false,
  ) {
    let totalDays = 0;
    if (this.firstSchooling) {
      if (excludingHolidays && dates) {
        dates.forEach((date) => {
          if (!date.term || date.status === SchoolStatus.CLOSED) {
            return;
          }
          const currDay = moment(date.date);
          if (currDay.isBefore(this.firstSchooling)) {
            return;
          }
          if (currDay.isAfter(asOnDate)) {
            return;
          }
          if (
            date.status === SchoolStatus.MORNING ||
            date.status === SchoolStatus.AFTERNOON
          ) {
            totalDays += 0.5;
            return;
          }
          totalDays++;
        });

        return Math.floor(totalDays / 5);
      }
      const weeks = moment(asOnDate).diff(this.firstSchooling, 'weeks');
      if (weeks < 0) {
        return;
      }
      return weeks;
    }
  }

  yearLevelOnDate(date: string) {
    const yearDiff = moment().year() - moment(date).year();
    // numericCurrentYearLevel is '' for no year level, which reads as 0 here.
    return Math.max(0, (this.numericCurrentYearLevel as number) - yearDiff);
  }

  @dependentKeyCompat
  @cached
  get preferredGender() {
    const preferredGender = this.fieldValue<string>('core:genderPreferred');
    if (!isEmpty(preferredGender) && preferredGender != 'L') {
      return preferredGender;
    }
    return this.gender;
  }

  @dependentKeyCompat
  @cached
  get gender() {
    const defaultField = 'core:genderLegal';
    let field = defaultField;
    switch (this.jurisdiction) {
      case Jurisdiction.NZ_MOE:
        field = 'nzMOE:gender';
        break;
    }
    let gender = this.fieldValue<string>(field);
    if (isEmpty(gender) && field !== defaultField) {
      gender = this.fieldValue<string>(defaultField);
    }
    return isEmpty(gender) ? 'N' : gender;
  }

  /**
   * genderClass returns a CSS class based on the user's set gender.
   */
  @computed('preferredGender', 'type')
  get genderClass() {
    if (this.type === PersonType.STAFF) {
      return '';
    }
    switch (this.preferredGender) {
      case 'M':
        return 'gender-male';
      case 'F':
        return 'gender-female';
    }
    return 'gender-neutral';
  }

  @computed('fieldOptions.yearLevelOptions.[]')
  get yearLevelOptions() {
    const yearLevelOptionsList = this.fieldOptions
      .yearLevelOptions as YearLevelOptionMap[];
    if (!isEmpty(yearLevelOptionsList)) {
      if (this.permissions.equal(Scopes.CAREGIVER)) {
        const schoolId = this.get('learner.schoolId');
        const optionMap = findBy(yearLevelOptionsList, 'schoolId', schoolId);
        if (isEmpty(optionMap)) {
          return;
        }
        return optionMap!.options;
      }
      return yearLevelOptionsList[0]!.options;
    }
  }

  @computed('numericCurrentYearLevel', 'yearLevelOptions')
  get currentYearLevel() {
    if (!isEmpty(this.yearLevelOptions)) {
      const option = findBy(
        this.yearLevelOptions!,
        'valueInt',
        this.numericCurrentYearLevel,
      );
      if (!isEmpty(option)) {
        return option!.label;
      }
    }
    return this.numericCurrentYearLevel;
  }

  @dependentKeyCompat
  @cached
  get numericCurrentYearLevel() {
    if (this.type !== PersonType.LEARNER) {
      return '';
    }
    let fieldLabel = 'core:currentYearLevel';
    switch (this.jurisdiction) {
      case Jurisdiction.NZ_MOE:
        fieldLabel = 'nzMOE:currentYearLevel';
        break;
    }
    return (this.field(fieldLabel)?.value as number | undefined) ?? '';
  }

  @computed('currentYearLevel')
  get ribbonText() {
    const year = this.currentYearLevel;
    if (year !== '') {
      return (isEmpty(this.yearLevelOptions) ? 'Year ' : '') + year;
    }
    return '';
  }

  // _homeroom returns the learner's homeroom without filtering (i.e. by year).
  // It reads the group from the store. It does not load it: the route or the
  // task that shows the homeroom must load the groups first.
  @computed('type', 'learner.homeroomId')
  get _homeroom() {
    if (this.type !== PersonType.LEARNER) {
      // Not a Learner - don't look for a homeroom ID.
      return null;
    }

    const homeroomId = this.get('learner.homeroomId') as string;
    if (isEmpty(homeroomId)) {
      // No homeroom ID set - don't try to look it up.
      return null;
    }

    const group = this.store.peekRecord('group', homeroomId);
    warn(
      `The homeroom ${homeroomId} is not in the store. Load the groups in the route afterModel or in the task that shows the homeroom.`,
      group !== null,
      { id: 'linced.homeroom-not-loaded' },
    );
    return group;
  }

  // homeroom returns the learner's _current_ homeroom.
  @computed('_homeroom.year')
  get homeroom() {
    const group = this._homeroom;

    // Only return the group if it's in the current year.  This stops previous
    // years' groups being returned if they happen to be available and the
    // learner's homeroomId hasn't been updated (i.e. they haven't been placed
    // in a new homeroom, perhaps because they've left the school).
    // This doesn't account for schools using an academic year not aligned with
    // the calendar year, but their groups get moved into the new year anyway.
    if (isEmpty(group) || get(group, 'year') !== moment().year()) {
      return null;
    }

    return group;
  }

  @dependentKeyCompat
  @cached
  get bannerText() {
    let text = '';

    switch (this.type) {
      case PersonType.LEARNER:
        text = this.get('homeroom.label') as string;
        break;
      case PersonType.STAFF:
        text = this.fieldValue<string>('core:staffRole') ?? '';
        break;
    }

    return text;
  }

  @computed('currentYearLevel')
  get yearLevelText() {
    const year = this.currentYearLevel;
    if (!isEmpty(year)) {
      return (isEmpty(this.yearLevelOptions) ? '- Yr ' : '') + year;
    }
    return '';
  }

  // house is the custom:house field (not its value).
  @dependentKeyCompat
  get house() {
    return this.field('custom:house');
  }

  @computed('homeroom', 'bannerText', 'house')
  get bannerInfo() {
    let houseValue;
    const house = this.get('house');
    if (house) {
      switch (house.get('datatype')) {
        case DataType.STRING:
          houseValue = house.get('value');
          break;
        case DataType.LIST:
          houseValue = compact(
            house.get('value') as StatefulArray<string>,
          ).join(', ');
          break;
      }
    }
    return compact([
      this.get('homeroom.team'),
      this.bannerText,
      houseValue,
    ]).join(' | ');
  }

  @fragment('people/post-counts')
  declare postCounts: PostCounts | null;

  @cached
  get address() {
    const keys = ['address1', 'address2', 'address3', 'address4', 'postcode'];
    return compact(
      keys.map((key) => {
        const value = this.fieldValue<string>(`nzMOE:${key}`);
        if (isEmpty(value)) {
          // compact only removes null and undefined values
          return null;
        }
        return value;
      }),
    );
  }

  @dynamicField('core:phoneMobile', DataType.STRING)
  declare phoneNumber: string | undefined;

  // For role-based access.
  @array('string')
  declare roles: StatefulArray<string>;

  // Filter page access level role.
  @computed('type', 'roles.[]')
  get pageAccessLevel() {
    if (this.type !== PersonType.STAFF) {
      // Not a Staff - don't return a page access level.
      return null;
    }
    // Check each role and return the first one that matches our list of page access levels.
    // Return StaffPageAccess.RESTRICTED if we don't find one.
    let pageAccessLevel: string = StaffPageAccess.RESTRICTED;
    if (!isEmpty(this.roles)) {
      const allRoles = StaffPageAccess.getAllValues();
      this.roles.forEach((role) => {
        if ((allRoles as readonly string[]).includes(role)) {
          pageAccessLevel = role;
        }
      });
    }
    return pageAccessLevel;
  }

  // Remove all page access levels from roles and add given one.
  setPageAccessLevel(pageAccessLevel: string): void {
    const allRoles = StaffPageAccess.getAllValues();
    this.roles.removeObjects(allRoles).pushObject(pageAccessLevel);
  }
}
